import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Download, AlertCircle, Loader2, Clock } from "lucide-react";
import {
  FORM_KEY_BY_LIST, submitForm, fetchConsentCopy, fetchSaveSpendVideo, absoluteBackendUrl,
} from "../../lib/forms";
import { FREEBIES, storeFreebieAccess } from "@/lib/freebies";

const SUCCESS_STATES = ["success", "success_updated", "success_resource", "pending_update"];

// Unified lead-capture form: consent + privacy/disclosure links + all post-submit states.
// `list` is the exact Constant Contact display name; `source` is the page identifier.
export const LeadForm = ({ list, source, note, submitLabel = "Get Access", layout = "stacked", showDisclosure = true, successTitle, successMessage }) => {
  const formKey = FORM_KEY_BY_LIST[list];
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [consentCopy, setConsentCopy] = useState(null);
  const [errors, setErrors] = useState({});
  // Stable per-form-instance key so double-clicks/retries never duplicate.
  const idempotencyKey = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `lf-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => { Promise.resolve(fetchConsentCopy()).then(setConsentCopy); }, []);

  const trimmedFirstName = firstName.trim();
  const trimmedEmail = email.trim();
  const validationErrors = useMemo(
    () => validateLeadForm({ firstName: trimmedFirstName, email: trimmedEmail, consent }),
    [trimmedFirstName, trimmedEmail, consent]
  );
  const isValid = Object.keys(validationErrors).length === 0;

  useEffect(() => {
    if (Object.keys(errors).length > 0 && JSON.stringify(errors) !== JSON.stringify(validationErrors)) {
      setErrors(validationErrors);
    }
  }, [validationErrors, errors]);

  const showCurrentErrors = () => setErrors(validationErrors);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors(validationErrors);
    if (!isValid) return;

    setStatus("submitting");
    const data = await submitForm(formKey, {
      firstName: trimmedFirstName, email: trimmedEmail, consent, source, company,
      consentVersion: consentCopy?.version,
      idempotencyKey: idempotencyKey.current,
    });
    setResult(data);
    // For the five gated freebies, store the secure (signed, time-limited) access
    // link transiently and redirect to that freebie's dedicated thank-you page.
    const freebie = FREEBIES[formKey];
    if (data.status === "success_resource" && freebie) {
      storeFreebieAccess(formKey, data);
      navigate(freebie.slug);
      return;
    }
    // Newsletter: redirect to its branded thank-you page (no PII stored/passed).
    if (formKey === "newsletter" && SUCCESS_STATES.includes(data.status)) {
      navigate("/resources/thank-you/newsletter");
      return;
    }
    setStatus(data.status || "cc_error");
  };

  if (SUCCESS_STATES.includes(status)) {
    return <PostSubmit status={status} result={result} successTitle={successTitle} successMessage={successMessage} />;
  }

  const isError = status !== "idle" && status !== "submitting";
  const inline = layout === "inline";
  const firstErrorId = `${formKey}-first-error`;
  const emailErrorId = `${formKey}-email-error`;
  const consentErrorId = `${formKey}-consent-error`;

  return (
    <form onSubmit={onSubmit} data-testid={`lead-form-${formKey}`} noValidate className={inline ? "mt-9 text-left" : "mt-5 space-y-3"}>
      <div className={inline ? "grid sm:grid-cols-[1fr_1.2fr] gap-3" : "space-y-3"}>
        <div>
          <label htmlFor={`${formKey}-first`} className="sr-only">First name</label>
          <input
            id={`${formKey}-first`} type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => { setFirstName((value) => value.trim()); showCurrentErrors(); }}
            placeholder="First name" required autoComplete="given-name" aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? firstErrorId : undefined} data-testid={`lead-first-${formKey}`}
            className="w-full rounded-button border border-taupe/60 bg-white px-4 py-3 text-sm text-legacy placeholder:text-slate/60 focus:outline-none focus:border-gold"
          />
          {errors.firstName && <p id={firstErrorId} role="alert" className="mt-1 text-[0.75rem] text-bronze">{errors.firstName}</p>}
        </div>
        <div>
          <label htmlFor={`${formKey}-email`} className="sr-only">Email address</label>
          <input
            id={`${formKey}-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onBlur={() => { setEmail((value) => value.trim()); showCurrentErrors(); }}
            placeholder="Email address" required autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? emailErrorId : undefined} data-testid={`lead-email-${formKey}`}
            className="w-full rounded-button border border-taupe/60 bg-white px-4 py-3 text-sm text-legacy placeholder:text-slate/60 focus:outline-none focus:border-gold"
          />
          {errors.email && <p id={emailErrorId} role="alert" className="mt-1 text-[0.75rem] text-bronze">{errors.email}</p>}
        </div>
      </div>

      {/* Honeypot — visually hidden, must stay empty */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor={`${formKey}-company`}>Company</label>
        <input id={`${formKey}-company`} type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} />
      </div>

      <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
        <input
          type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); showCurrentErrors(); }}
          required aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? consentErrorId : undefined} data-testid={`lead-consent-${formKey}`}
          className="mt-1 h-4 w-4 shrink-0 rounded border-taupe/70 text-gold focus:ring-gold accent-[#8B6241]"
        />
        <span className="text-[0.78rem] leading-relaxed text-slate/90">
          {consentCopy?.text || "Yes, I agree to receive educational emails from Legacy Life & Retirement. I understand I can unsubscribe at any time."}{" "}
          See our <Link to="/privacy" className="text-bronze underline hover:text-gold">Privacy Policy</Link>
          {showDisclosure && <> and <Link to="/disclosures" className="text-bronze underline hover:text-gold">Disclosures</Link></>}.
        </span>
      </label>
      {errors.consent && <p id={consentErrorId} role="alert" className="text-[0.75rem] text-bronze">{errors.consent}</p>}

      {isError && (
        <p role="alert" data-testid={`lead-error-${formKey}`} className="flex items-start gap-2 text-[0.8rem] text-bronze">
          <AlertCircle size={15} className="shrink-0 mt-0.5" />{result?.message || "Something went wrong. Please try again."}
        </p>
      )}

      <button type="submit" disabled={status === "submitting" || !isValid} data-testid={`lead-submit-${formKey}`} className={`btn-base btn-primary ${inline ? "w-full sm:w-auto mt-3" : "w-full"} text-sm py-3 disabled:opacity-60`}>
        {status === "submitting" ? <><Loader2 size={16} className="animate-spin" />Submitting…</> : submitLabel}
      </button>

      <p className="text-[0.7rem] tracking-wide text-slate/70">{note || `List: ${list}`}</p>
    </form>
  );
};

const PostSubmit = ({ status, result, successTitle, successMessage }) => {
  if (status === "pending_update") {
    return (
      <div data-testid="lead-state-pending" className="mt-5 rounded-card border border-bronze/40 bg-[#EFE8D9] p-5">
        <Clock size={20} className="text-bronze" />
        <p className="mt-2 text-slate text-[0.9rem] leading-relaxed">{result.message}</p>
      </div>
    );
  }
  if (status === "success_resource" && result.resource_type === "video") {
    return <VideoReveal accessUrl={result.access_url} />;
  }
  if (status === "success_resource") {
    return (
      <div data-testid="lead-state-download" className="mt-5 rounded-card border border-gold/40 bg-white p-5">
        <Check size={20} className="text-gold" />
        <p className="mt-2 text-legacy text-[0.92rem] font-medium">{result.message}</p>
        <a href={absoluteBackendUrl(result.download_url)} target="_blank" rel="noopener noreferrer" data-testid="lead-download-link" className="btn-base btn-gold mt-4 text-sm py-3">
          <Download size={16} />Download {result.resource_name}
        </a>
        <p className="mt-3 text-[0.7rem] text-slate/70">This is a secure, time-limited link. It will expire shortly for your protection.</p>
      </div>
    );
  }
  return (
    <div data-testid="lead-state-success" className="mt-5 rounded-card border border-gold/40 bg-white p-5">
      <Check size={20} className="text-gold" />
      <p className="mt-2 text-legacy text-[0.92rem] font-medium">{successTitle || result.message}</p>
      {successMessage && <p className="mt-2 text-slate text-[0.86rem] leading-relaxed">{successMessage}</p>}
    </div>
  );
};

const VideoReveal = ({ accessUrl }) => {
  const [videoId, setVideoId] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    fetchSaveSpendVideo(accessUrl).then((d) => {
      if (d.status === "ok") setVideoId(d.video_id); else setError(true);
    }).catch(() => setError(true));
  }, [accessUrl]);

  return (
    <div data-testid="lead-state-video" className="mt-5 rounded-card border border-gold/40 bg-white p-5">
      <Check size={20} className="text-gold" />
      <p className="mt-2 text-legacy text-[0.92rem] font-medium">Your Save &amp; Spend System Overview is ready.</p>
      <div className="mt-4 aspect-video w-full rounded-card overflow-hidden bg-black border border-taupe/40">
        {videoId ? (
          <iframe
            className="h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
            title="Save & Spend System Overview"
            loading="lazy" allowFullScreen
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            data-testid="save-spend-iframe"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-taupe text-sm px-6 text-center">
            {error ? "This overview is temporarily unavailable. Please request it again." : "Loading your overview…"}
          </div>
        )}
      </div>
    </div>
  );
};

export const validateLeadForm = ({ firstName, email, consent }) => {
  const errors = {};
  if (!firstName) errors.firstName = "Please enter your first name.";
  if (!email) {
    errors.email = "Please enter your email address.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = "Please enter a valid email address.";
  }
  if (!consent) errors.consent = "Please confirm your consent to receive educational emails.";
  return errors;
};
